import { queryClient } from '@/queryClient';

/**
 * Invalidate the MuvalTable list queries for an endpoint.
 *
 * MuvalTable keys its query on the full `url` prop, which usually carries query
 * params (e.g. `/transports?method=ROAD&include_containers=true`), so a store
 * can't reconstruct the exact key — match on the path prefix instead.
 *
 * Not needed just to keep an index fresh after a form saves and routes back to
 * it: MuvalTable refetches on every mount (`refetchOnMount: 'always'`), so the
 * revisited index reloads its rows on its own.
 *
 * Still needed when the rows are on screen while they change, or when the key
 * is shared by a query that is not a MuvalTable:
 * - a table that stays mounted while a modal, drawer or side panel saves a row;
 * - non-table queries sharing the prefix (e.g. the Leads kanban below), which
 *   keep the global 30s `staleTime` (src/queryClient.ts) and do not refetch on
 *   mount.
 * Entity stores that already call it after a mutation (transport, storage) keep
 * it as a direct guarantee for their own index.
 */
export function invalidateTableQueries(path: string): void {
    queryClient.invalidateQueries({
        predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith(path),
    });
}

/**
 * Cache-key prefixes for every list surface that renders a row's move fields —
 * the category chip, space, dates. `/orders/list` is the Leads index table,
 * `/orders/jobs` the Jobs and Affiliate-leads tables, `/global-search/orders`
 * the search dropdown, and `kanban` covers both the Leads kanban board query
 * (`kanban-board`) and the per-column tray queries (`kanban`).
 */
const ORDER_LIST_QUERY_PREFIXES = ['/orders/list', '/orders/jobs', '/global-search/orders', 'kanban'];

/**
 * Invalidate the cached order lists after a move mutation.
 *
 * Editing a move from the order page (e.g. the move category changer) leaves
 * those lists holding pre-edit rows: they're inactive queries while the order
 * page is open, so this only marks them stale and they refetch on next mount
 * rather than firing a request now. The MuvalTable lists would refetch on mount
 * regardless; the kanban queries would not, which is what keeps this necessary.
 */
export function invalidateOrderListQueries(): void {
    ORDER_LIST_QUERY_PREFIXES.forEach(invalidateTableQueries);
}
